/**
 * StoryReader Component
 *
 * Displays a detective story for reading with a clean,
 * immersive reading experience for kids.
 */

import { useState } from 'react'
import { motion } from 'framer-motion'
import { BookOpen, Clock, ChevronDown, ChevronUp } from 'lucide-react'
import { Button, Card, CardContent } from '@/components/ui'
import type { DetectiveStory } from '@/types/database'

interface StoryReaderProps {
  story: DetectiveStory
  onFinishReading?: () => void
  showFinishButton?: boolean
}

export function StoryReader({
  story,
  onFinishReading,
  showFinishButton = true,
}: StoryReaderProps) {
  const [isExpanded, setIsExpanded] = useState(true)

  // Split content into paragraphs
  const paragraphs = story.story_content
    .split('\n\n')
    .filter(p => p.trim().length > 0)

  return (
    <Card className="overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[var(--color-kids-primary)] to-[var(--color-kids-secondary)] p-4 text-white">
        <div className="flex items-start gap-3">
          <div className="text-4xl">{story.cover_emoji}</div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display font-bold text-xl">{story.title}</h2>
            <p className="text-white/80 text-sm">by {story.author}</p>
            <div className="flex items-center gap-3 mt-2 text-sm text-white/70">
              <span className="flex items-center gap-1">
                <BookOpen className="w-4 h-4" />
                {story.word_count} words
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                ~{story.reading_time_minutes} min read
              </span>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-white hover:bg-white/20"
          >
            {isExpanded ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </Button>
        </div>
      </div>

      {/* Story content */}
      {isExpanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <CardContent className="p-6">
            <div className="prose prose-lg max-w-none">
              {paragraphs.map((paragraph, index) => (
                <motion.p
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.3 }}
                  className="text-[var(--color-kids-text)] text-lg leading-relaxed mb-4 last:mb-0"
                >
                  {paragraph}
                </motion.p>
              ))}
            </div>

            {showFinishButton && onFinishReading && (
              <div className="mt-6 pt-4 border-t border-[var(--color-kids-surface-alt)] text-center">
                <Button onClick={onFinishReading} size="lg">
                  I've finished reading!
                </Button>
              </div>
            )}
          </CardContent>
        </motion.div>
      )}
    </Card>
  )
}

// =============================================================================
// Story Card for Hub
// =============================================================================

interface DetectiveStoryCardProps {
  story: DetectiveStory
  hasAttempted?: boolean
  bestScore?: number | null
  onClick?: () => void
}

export function DetectiveStoryCard({
  story,
  hasAttempted = false,
  bestScore,
  onClick,
}: DetectiveStoryCardProps) {
  const difficultyColors = {
    beginner: 'bg-[var(--color-kids-success)]/10 text-[var(--color-kids-success)]',
    intermediate: 'bg-[var(--color-kids-secondary)]/10 text-[var(--color-kids-secondary)]',
    advanced: 'bg-[var(--color-kids-accent)]/10 text-[var(--color-kids-accent)]',
  }

  return (
    <motion.button
      onClick={onClick}
      className="w-full text-left"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <Card className="overflow-hidden hover:shadow-lg transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            {/* Cover emoji */}
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[var(--color-kids-primary)]/10 to-[var(--color-kids-secondary)]/10 flex items-center justify-center text-3xl flex-shrink-0">
              {story.cover_emoji}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-display font-bold text-[var(--color-kids-text)] truncate">
                  {story.title}
                </h3>
                {story.is_featured && (
                  <span className="text-xs bg-[var(--color-kids-accent)]/10 text-[var(--color-kids-accent)] px-2 py-0.5 rounded-full">
                    Featured
                  </span>
                )}
              </div>

              <p className="text-sm text-[var(--color-kids-text-muted)] mb-2">
                by {story.author}
              </p>

              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs px-2 py-0.5 rounded-full ${difficultyColors[story.difficulty]}`}>
                  {story.difficulty.charAt(0).toUpperCase() + story.difficulty.slice(1)}
                </span>
                <span className="text-xs text-[var(--color-kids-text-muted)]">
                  {story.reading_time_minutes} min read
                </span>
                {hasAttempted && (
                  <span className="text-xs bg-[var(--color-kids-success)]/10 text-[var(--color-kids-success)] px-2 py-0.5 rounded-full">
                    {bestScore !== null ? `Best: ${bestScore}/5` : 'Attempted'}
                  </span>
                )}
              </div>
            </div>

            {/* Status indicator */}
            <div className="flex-shrink-0">
              {hasAttempted ? (
                <div className="w-8 h-8 rounded-full bg-[var(--color-kids-success)]/10 flex items-center justify-center">
                  <span className="text-lg">
                    {bestScore === 5 ? '🌟' : '✓'}
                  </span>
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full bg-[var(--color-kids-surface-alt)] flex items-center justify-center">
                  <span className="text-lg">🔍</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.button>
  )
}

// =============================================================================
// Compact Story Card
// =============================================================================

interface CompactStoryCardProps {
  story: DetectiveStory
  onClick?: () => void
}

export function CompactStoryCard({ story, onClick }: CompactStoryCardProps) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 p-3 rounded-xl bg-[var(--color-kids-surface)] hover:bg-[var(--color-kids-surface-alt)] transition-colors text-left w-full"
    >
      <div className="text-2xl">{story.cover_emoji}</div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-[var(--color-kids-text)] truncate">
          {story.title}
        </p>
        <p className="text-xs text-[var(--color-kids-text-muted)]">
          {story.reading_time_minutes} min
        </p>
      </div>
    </button>
  )
}
